'use client';

import { useState, useRef, useEffect } from 'react';

import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { useLanguageStore, SUPPORTED_LANGUAGES, type LanguageCode } from '../../stores/languageStore';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { useTeamStore } from '../../stores/teamStore';
import { teamApi } from '../../api/team';
import { authApi } from '../../services/api';
import type { Team } from '../../types/team';
import i18n, { storeCodeToLocale } from '../../i18n';
import { useToastActions } from '../ui/Toast';
import { getAvatarUrl } from '../../utils/avatar';
import { isLocalDeployment } from '../../config';
import { getRegionProfile } from '../../config/region';
import { 
  FlowArrow, 
  SignOut, 
  User,
  Users,
  House,
  CaretLeft,
  CaretRight,
  Gear,
  Crown,
  Translate,
  Check,
  CreditCard,
  ChartBar,
  FileText,
} from '@phosphor-icons/react';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string; weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone' }>;
}

const navItems: NavItem[] = [
  { path: '/home', label: 'nav.home', icon: House },
  { path: '/workflows', label: 'nav.workflows', icon: FlowArrow },
  // { path: '/developer', label: 'nav.developer', icon: Code },
  { path: '/templates', label: 'nav.templates', icon: FileText },
];

// Subscription 分组菜单项
const subscriptionNavItems: NavItem[] = [
  { path: '/subscription/billing', label: 'nav.billing', icon: CreditCard },
  { path: '/subscription/usage', label: 'nav.usage', icon: ChartBar },
];

const SIDEBAR_EXPANDED = 288; // w-72
const SIDEBAR_COLLAPSED = 64;  // w-16

// Animated Nav Link with spring physics
function AnimatedNavLink({ item, isActive, collapsed }: { item: NavItem; isActive: boolean; collapsed: boolean }) {
  const Icon = item.icon;
  const { t } = useTranslation();
  const label = t(item.label);
  return (
    <NavLink
      to={item.path}
      title={collapsed ? label : undefined}
      className={`relative flex items-center py-3 rounded-xl text-sm font-medium transition-colors duration-200 ${collapsed ? 'justify-center px-0' : 'gap-3 px-4'}`}
    >
      {isActive && (
        <motion.div
          layoutId="activeNav"
          className="absolute inset-0 bg-zinc-900 rounded-xl"
          initial={false}
          transition={{
            type: 'spring',
            stiffness: 500,
            damping: 35,
          }}
        />
      )}
      <span className={`relative z-10 flex items-center gap-3 ${isActive ? 'text-white' : 'text-zinc-600 hover:text-zinc-900'}`}>
        <Icon className="w-5 h-5 flex-shrink-0" weight={isActive ? 'fill' : 'regular'} />
        <AnimatePresence initial={false}>
          {!collapsed && (
            <motion.span
              key="label"
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden whitespace-nowrap"
            >
              {label}
            </motion.span>
          )}
        </AnimatePresence>
      </span>
    </NavLink>
  );
}

// 计划名称映射 - 在组件内部使用 t 函数
const getPlanName = (plan: string, t: (key: string) => string): string => {
  const planNames: Record<string, string> = {
    basic: t('common.basic') || t('common.free') || '基础版',
    free: t('common.free') || '免费版',
    pro: t('common.pro') || '专业版',
    enterprise: t('common.enterprise') || '企业版',
  };
  return planNames[plan] || plan;
};

export function MainLayout() {
  const { user, logout, isAuthenticated, setToken } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [, setUserMenuPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { success: toastSuccess } = useToastActions();
  
  const { subscription, fetchSubscription, clearSubscription } = useSubscriptionStore();
  const { currentTeamId, setCurrentTeamId } = useTeamStore();
  const currentPlan = subscription?.plan ?? 'basic';

  const handleLogout = () => {
    clearSubscription();
    logout();
    navigate('/login');
  };

  const handleSettings = () => {
    navigate('/settings');
    setShowUserMenu(false);
  };



  // 语言设置 - 使用全局状态
  const { language, setLanguage } = useLanguageStore();
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  // 团队切换
  const [teams, setTeams] = useState<Team[]>([]);
  const [showTeamMenu, setShowTeamMenu] = useState(false);
  const [currentTeamName, setCurrentTeamName] = useState<string>('');

  // 获取团队列表
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const fetchTeams = async () => {
      try {
        const response = await teamApi.listTeams();
        // console.log('[Team] listTeams response:', response);
        if (response.code === 0 && response.data) {
          const teamsData = response.data.teams ?? [];
          setTeams(teamsData);
          // 当前团队名称：个人模式显示「个人」，否则显示团队名
          if (!currentTeamId) {
            setCurrentTeamName(t('nav.personal') || '个人');
          } else {
            const current = teamsData.find((t) => t.id === currentTeamId);
            setCurrentTeamName(current?.name ?? '');
          }
        }
      } catch (err) {
        console.warn('Failed to fetch teams:', err);
      }
    };

    fetchTeams();
  }, [isAuthenticated, currentTeamId]);

  const handleUpgrade = () => {
    navigate('/upgrade');
    setShowUserMenu(false);
  };



  const handleLanguage = () => {
    setShowLanguageMenu(true);
  };

  const handleTeamSwitch = () => {
    setShowTeamMenu(true);
  };

  const handleSwitchTeam = async (teamId: string) => {
    try {
      const response = await teamApi.switchTeam(teamId);
      console.log('switch team info is ', response);

      if (response.code === 0 && response.data) {
        if (response.data.tokens) {
          // 先保存 token 到 localStorage
          localStorage.setItem('token', response.data.tokens.access_token);
          // 更新 authStore 中的 token
          setToken(response.data.tokens.access_token);
        }

        // 显示切换成功提示（在刷新前）
        const isPersonal = teamId === '';
        if (isPersonal) {
          toastSuccess(t('nav.switchedToPersonal') || '已切换到个人模式');
        } else {
          const team = teams.find(t => t.id === teamId) ?? response.data.team;
          toastSuccess(t('nav.teamSwitched', { name: team?.name }) || `已切换到团队：${team?.name}`);
        }

        // 更新 currentTeamId（在刷新前更新，确保页面刷新后状态正确）
        setCurrentTeamId(isPersonal ? "" : teamId);

        // 延迟刷新页面，确保状态已更新
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        toastSuccess(t('nav.teamSwitchFailed') || '切换团队失败');
      }
    } catch (err) {
      console.error('Failed to switch team:', err);
      toastSuccess(t('nav.teamSwitchFailed') || '切换团队失败');
    }
    setShowTeamMenu(false);
    setShowUserMenu(false);
  };

  const openUserMenu = () => {
    const el = userMenuRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      const menuHeight = 280;
      setUserMenuPosition({
        top: rect.top - menuHeight - 8,
        left: rect.left,
        width: collapsed ? 192 : rect.width,
      });
      setShowUserMenu(true);
    }
  };

  const toggleUserMenu = () => {
    if (showUserMenu) {
      setShowUserMenu(false);
      setShowLanguageMenu(false);
      setShowTeamMenu(false);
      setUserMenuPosition(null);
    } else {
      openUserMenu();
    }
  };

  const { t } = useTranslation();
  
  // 获取区域配置
  const profile = getRegionProfile();
  const isCN = profile.region === 'cn';
  
  const handleLanguageSelect = async (langCode: string) => {
    try {
      // 先更新前端语言状态
      setLanguage(langCode as LanguageCode);
      i18n.changeLanguage(storeCodeToLocale(langCode));
      
      // 调用 API 切换语言并获取新 token
      const response = await authApi.switchLanguage(langCode);
      if (response.code === 0 && response.data?.tokens) {
        // 保存新 token
        localStorage.setItem('token', response.data.tokens.access_token);
        // 更新 authStore 中的 token
        setToken(response.data.tokens.access_token);
      }
      
      setShowLanguageMenu(false);
      toastSuccess(t('language.switched', '语言切换成功'));
    } catch (err) {
      console.error('Failed to switch language:', err);
      // 即使 API 调用失败，语言切换仍然生效
      setShowLanguageMenu(false);
      toastSuccess(t('language.switched', '语言切换成功'));
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // 默认个人模式，不自动选择第一个团队（currentTeamId 由登录或切换决定）

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
        setShowLanguageMenu(false);
        setShowTeamMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;

  return (
    <div
      className="h-full bg-zinc-50 flex overflow-hidden"
      style={{ ['--app-sidebar-width' as string]: `${sidebarWidth}px` }}
    >
      {/* Sidebar - Bento Style */}
      <motion.aside 
        className="fixed left-0 top-0 h-full bg-white border-r border-zinc-200/50 z-50 flex flex-col overflow-hidden"
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1, width: sidebarWidth }}
        transition={{ type: 'spring', stiffness: 100, damping: 25 }}
      >
        {/* Logo Area */}
        <div className={`p-4 flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
          <motion.div 
            className={`flex items-center gap-3 ${collapsed ? '' : 'px-2'}`}
            whileHover={{ scale: 1.02 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <div className="w-10 h-10 rounded-xl bg-zinc-900 flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img src="/logo.svg" alt="Logo" className="w-6 h-6" />
            </div>
            <AnimatePresence initial={false}>
              {!collapsed && (
                <motion.div
                  key="logo-text"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <span className="text-lg font-bold text-zinc-900 tracking-tight whitespace-nowrap">{t('app.name') || '表格流'}</span>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Collapse Toggle Button */}
          {!collapsed && (
            <motion.button
              onClick={() => setCollapsed(true)}
              className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors flex-shrink-0"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title={t('nav.collapseSidebar') || '折叠侧栏'}
            >
              <CaretLeft className="w-4 h-4" />
            </motion.button>
          )}
        </div>

        {/* Expand button when collapsed */}
        {collapsed && (
          <div className="flex justify-center pb-2">
            <motion.button
              onClick={() => setCollapsed(false)}
              className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title={t('nav.expandSidebar') || '展开侧栏'}
            >
              <CaretRight className="w-4 h-4" />
            </motion.button>
          </div>
        )}

        {/* Navigation */}
        <nav className={`flex-1 py-4 space-y-1 ${collapsed ? 'px-2' : 'px-4'}`}>
          <div className="mb-6">
            <AnimatePresence initial={false}>
              {!collapsed && (
                <motion.p
                  key="nav-label"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="px-4 text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3"
                >
                  {t('nav.mainMenu') || '主菜单'}
                </motion.p>
              )}
            </AnimatePresence>
            <div className="space-y-1">
              {navItems.map((item) => (
                <AnimatedNavLink
                  key={item.path}
                  item={item}
                  isActive={location.pathname.startsWith(item.path)}
                  collapsed={collapsed}
                />
              ))}
            </div>
          </div>

          {/* Subscription 分组 */}
          {!isLocalDeployment && !isCN && (
            <div className="mb-6">
              <AnimatePresence initial={false}>
                {!collapsed && (
                  <motion.p
                    key="subscription-nav-label"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="px-4 text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3"
                  >
                    {t('nav.subscription') || '订阅'}
                  </motion.p>
                )}
              </AnimatePresence>
              <div className="space-y-1">
                {subscriptionNavItems.map((item) => (
                  <AnimatedNavLink
                    key={item.path}
                    item={item}
                    isActive={location.pathname.startsWith(item.path)}
                    collapsed={collapsed}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Quick Stats - Bento Card */}
          {/* <AnimatePresence initial={false}>
            {!collapsed && (
              <motion.div
                key="stats-card"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="px-4"
              >
                <motion.div 
                  className="p-4 rounded-2xl bg-zinc-50 border border-zinc-100"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <p className="text-xs font-medium text-zinc-500 mb-3">今日处理</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-zinc-900">2,847</span>
                    <span className="text-xs text-emerald-600 font-medium">+12%</span>
                  </div>
                  <div className="mt-3 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                    <motion.div 
                      className="h-full bg-emerald-500 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: '68%' }}
                      transition={{ delay: 0.5, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    />
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence> */}
        </nav>

        {/* User Profile - Bottom */}
        <div className={`p-3 border-t border-zinc-100 ${collapsed ? 'flex justify-center' : ''}`} ref={userMenuRef}>
          {collapsed ? (
            <div className="relative">
              <motion.button
                onClick={toggleUserMenu}
                className="relative group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title={user?.nickname || user?.username || t('common.user') || '用户'}
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-50 flex items-center justify-center border border-emerald-100 cursor-pointer overflow-hidden">
                  {user?.avatar ? (
                    <img src={getAvatarUrl(user.avatar)} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-emerald-600" weight="fill" />
                  )}
                </div>
              </motion.button>
              
              {/* Collapsed User Menu Popup */}
              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 10 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    className="absolute bottom-full left-0 mb-2 w-48 bg-white rounded-xl border border-zinc-200 shadow-xl shadow-zinc-900/10 py-1 z-50"
                  >
                    <div className="px-3 py-2 border-b border-zinc-100">
                      <p className="text-sm font-medium text-zinc-900">{user?.nickname || user?.username || '用户'}</p>
                      <p className="text-xs text-zinc-400">{getPlanName(currentPlan, t)}</p>
                    </div>
                    {!isLocalDeployment && !isCN && (
                      <>
                        {/* <button onClick={handleSubscription} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                          <Crown className="w-4 h-4 text-amber-500" />
                          {t('nav.subscription') || '订阅管理'}
                        </button> */}
                        <button onClick={handleUpgrade} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                          <Crown className="w-4 h-4 text-amber-500" />
                          {t('nav.subscriptionUpgrade') || '订阅升级'}
                        </button>
                        {/* <button onClick={handleFeedback} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                          <ChatCircleText className="w-4 h-4 text-blue-500" />
                          {t('nav.userFeedback') || '用户反馈'}
                        </button> */}
                      </>
                    )}
                    {/* <button onClick={handleTeam} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                      <Users className="w-4 h-4 text-indigo-500" />
                      {t('nav.myTeam') || '我的团队'}
                    </button> */}
                    <button onClick={handleTeamSwitch} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                      <Users className="w-4 h-4 text-blue-500" />
                      {t('nav.switchTeam') || '切换团队'}
                      <span className="ml-auto text-xs text-zinc-400 truncate max-w-[80px]">{currentTeamName}</span>
                    </button>
                    <button onClick={handleLanguage} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                      <Translate className="w-4 h-4 text-purple-500" />
                      {t('nav.language')}
                    </button>
                    {isLocalDeployment && user?.role === 'admin' && (
                      <button onClick={() => { navigate('/users'); setShowUserMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                        <Users className="w-4 h-4 text-emerald-500" />
                        {t('nav.userManagement')}
                      </button>
                    )}
                    <button onClick={handleSettings} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                      <Gear className="w-4 h-4 text-zinc-500" />
                      {t('nav.settings')}
                    </button>
                    <div className="border-t border-zinc-100 mt-1 pt-1">
                      <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
                        <SignOut className="w-4 h-4" />
                        {t('nav.logout')}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Team Switch Submenu (Collapsed) */}
              <AnimatePresence>
                {showUserMenu && showTeamMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    className="absolute bottom-full left-0 mb-2 w-48 bg-white rounded-xl border border-zinc-200 shadow-xl shadow-zinc-900/10 py-1 z-50"
                  >
                    <div className="px-3 py-2 border-b border-zinc-100 flex items-center justify-between">
                      <p className="text-sm font-medium text-zinc-900">{t('nav.selectTeam') || '选择团队'}</p>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setShowTeamMenu(false); }}
                        className="text-xs text-zinc-400 hover:text-zinc-600"
                      >
                        {t('nav.backToMenu') || '返回'}
                      </button>
                    </div>
                    <button
                      onClick={() => handleSwitchTeam('')}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                        !currentTeamId ? 'bg-emerald-50 text-emerald-700' : 'text-zinc-700 hover:bg-zinc-50'
                      }`}
                    >
                      <User className="w-4 h-4" />
                      <span className="truncate">{t('nav.personal') || '个人'}</span>
                      {!currentTeamId && <Check className="w-4 h-4 ml-auto" weight="bold" />}
                    </button>
                    {teams.map((team) => (
                      <button
                        key={team.id}
                        onClick={() => handleSwitchTeam(team.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                          currentTeamId === team.id ? 'bg-emerald-50 text-emerald-700' : 'text-zinc-700 hover:bg-zinc-50'
                        }`}
                      >
                        <Users className="w-4 h-4" />
                        <span className="truncate">{team.name}</span>
                        {currentTeamId === team.id && (
                          <Check className="w-4 h-4 ml-auto" weight="bold" />
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Language Submenu (Collapsed) - 替换主菜单内容 */}
              <AnimatePresence>
                {showUserMenu && showLanguageMenu && !showTeamMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    className="absolute bottom-full left-0 mb-2 w-48 bg-white rounded-xl border border-zinc-200 shadow-xl shadow-zinc-900/10 py-1 z-50"
                  >
                    <div className="px-3 py-2 border-b border-zinc-100 flex items-center justify-between">
                      <p className="text-sm font-medium text-zinc-900">{t('nav.selectLanguage') || '选择语言'}</p>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setShowLanguageMenu(false); }}
                        className="text-xs text-zinc-400 hover:text-zinc-600"
                      >
                        {t('nav.backToMenu') || '返回'}
                      </button>
                    </div>
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageSelect(lang.code)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                          language === lang.code ? 'bg-emerald-50 text-emerald-700' : 'text-zinc-700 hover:bg-zinc-50'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span>{lang.name}</span>
                        {language === lang.code && (
                          <Check className="w-4 h-4 ml-auto" weight="bold" />
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <div className="relative">
              <motion.button 
                onClick={toggleUserMenu}
                className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-zinc-50 transition-colors cursor-pointer group"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-50 flex items-center justify-center border border-emerald-100 overflow-hidden">
                    {user?.avatar ? (
                      <img src={getAvatarUrl(user.avatar)} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-5 h-5 text-emerald-600" weight="fill" />
                    )}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-zinc-900">
                      {user?.nickname || user?.username || '用户'}
                    </p>
                    <p className="text-xs text-zinc-400">{getPlanName(currentPlan, t)}</p>
                  </div>
                </div>
                <Gear className="w-4 h-4 text-zinc-400 group-hover:text-zinc-600 transition-colors" />
              </motion.button>
              
              {/* Expanded User Menu Popup */}
              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 10 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-xl border border-zinc-200 shadow-xl shadow-zinc-900/10 py-1 z-50"
                  >
                    {!isLocalDeployment && !isCN && (
                      <>
                        {/* <button onClick={handleSubscription} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                          <Crown className="w-4 h-4 text-amber-500" />
                          {t('nav.subscription') || '订阅管理'}
                        </button> */}
                        <button onClick={handleUpgrade} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                          <Crown className="w-4 h-4 text-amber-500" />
                          {t('nav.subscriptionUpgrade')}
                        </button>
                        {/* <button onClick={handleFeedback} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                          <ChatCircleText className="w-4 h-4 text-blue-500" />
                          {t('nav.userFeedback')}
                        </button> */}
                      </>
                    )}
                    {/* <button onClick={handleTeam} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                      <Users className="w-4 h-4 text-indigo-500" />
                      {t('nav.myTeam') || '我的团队'}
                    </button> */}
                    <button onClick={handleTeamSwitch} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                      <Users className="w-4 h-4 text-blue-500" />
                      {t('nav.switchTeam') || '切换团队'}
                      <span className="ml-auto text-xs text-zinc-400 truncate max-w-[100px]">{currentTeamName}</span>
                    </button>
                    <button onClick={handleLanguage} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                      <Translate className="w-4 h-4 text-purple-500" />
                      {t('nav.language')}
                    </button>
                    {isLocalDeployment && user?.role === 'admin' && (
                      <button onClick={() => { navigate('/users'); setShowUserMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                        <Users className="w-4 h-4 text-emerald-500" />
                        {t('nav.userManagement')}
                      </button>
                    )}
                    <button onClick={handleSettings} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                      <Gear className="w-4 h-4 text-zinc-500" />
                      {t('nav.settings')}
                    </button>
                    <div className="border-t border-zinc-100 mt-1 pt-1">
                      <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
                        <SignOut className="w-4 h-4" />
                        {t('nav.logout')}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Team Switch Submenu (Expanded) */}
              <AnimatePresence>
                {showUserMenu && showTeamMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-xl border border-zinc-200 shadow-xl shadow-zinc-900/10 py-1 z-50"
                  >
                    <div className="px-3 py-2 border-b border-zinc-100 flex items-center justify-between">
                      <p className="text-sm font-medium text-zinc-900">{t('nav.selectTeam') || '选择团队'}</p>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setShowTeamMenu(false); }}
                        className="text-xs text-zinc-400 hover:text-zinc-600"
                      >
                        {t('nav.back')}
                      </button>
                    </div>
                    <button
                      onClick={() => handleSwitchTeam('')}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                        !currentTeamId ? 'bg-emerald-50 text-emerald-700' : 'text-zinc-700 hover:bg-zinc-50'
                      }`}
                    >
                      <User className="w-4 h-4" />
                      <span className="truncate">{t('nav.personal') || '个人'}</span>
                      {!currentTeamId && <Check className="w-4 h-4 ml-auto" weight="bold" />}
                    </button>
                    {teams.map((team) => (
                      <button
                        key={team.id}
                        onClick={() => handleSwitchTeam(team.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                          currentTeamId === team.id ? 'bg-emerald-50 text-emerald-700' : 'text-zinc-700 hover:bg-zinc-50'
                        }`}
                      >
                        <Users className="w-4 h-4" />
                        <span className="truncate">{team.name}</span>
                        {currentTeamId === team.id && (
                          <Check className="w-4 h-4 ml-auto" weight="bold" />
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Language Submenu (Expanded) - 替换主菜单内容 */}
              <AnimatePresence>
                {showUserMenu && showLanguageMenu && !showTeamMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-xl border border-zinc-200 shadow-xl shadow-zinc-900/10 py-1 z-50"
                  >
                    <div className="px-3 py-2 border-b border-zinc-100 flex items-center justify-between">
                      <p className="text-sm font-medium text-zinc-900">{t('nav.selectLanguage')}</p>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setShowLanguageMenu(false); }}
                        className="text-xs text-zinc-400 hover:text-zinc-600"
                      >
                        {t('nav.back')}
                      </button>
                    </div>
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageSelect(lang.code)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                          language === lang.code ? 'bg-emerald-50 text-emerald-700' : 'text-zinc-700 hover:bg-zinc-50'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span>{lang.name}</span>
                        {language === lang.code && (
                          <Check className="w-4 h-4 ml-auto" weight="bold" />
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </motion.aside>

      {/* Main Content Area */}
      <motion.main
        className="flex-1 h-full overflow-hidden bg-zinc-50"
        animate={{ marginLeft: sidebarWidth }}
        transition={{ type: 'spring', stiffness: 100, damping: 25 }}
      >
        <Outlet />
      </motion.main>
    </div>
  );
}
